import React, { useEffect } from 'react';
import intl from '$intl';
import { Flex } from '$gstyles/wrapper';
import { Autocomplete } from '$gcomponents/reusables';
import { Div, SPACE } from '$gstyles';
import { getPublicFactoryId } from '$gbusiness/helpers/util';
import { configs } from '$configs';
import { Button } from '$gcomponents/primitives';
import { getEl } from '$gbusiness/helpers/input';
import { IonIcon } from '@ionic/react';
import { barcodeOutline } from 'ionicons/icons';
import { deriveRawToItem, initialItem } from '$fbusiness/models/item';

interface ItemPickerProps {
  scanMode: boolean;
  disabled: boolean;
  onClickScan: Function;
  onScanFetch: Function;
  onSelectItem: Function;
  enableBlank?: boolean;
  scanOnlyMode?: boolean;
  storeId?: number;
}

export const AUTO_COMPLETE = (onClickItem) => ({
  method: 'POST',
  minChar: 2,
  renderSuggestion: (e) => {
    return (
      <Flex justifyContent="space-between">
        {e.sku == null ? (
          <>
            <Div marginLeft={SPACE.SMALL}>{e.name} </Div>
            {e.id ? (
              <Button
                className="primary filled"
                styles={{ backgroundColor: '#000' }}
                size="small"
                color="secondary"
                onClick={() => {
                  /*onClickItem(e)*/
                }}>
                {intl('SCREEN.PO.BUTTON.ADD')}
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <div>{`${e.name} (${e.sku}) ${e.barcode && ` - ${e.barcode}`}`}</div>
            {e.outOfStock ? (
              <div>{intl('COMMON.OUT_OF_STOCK')}</div>
            ) : (
              <Button
                className="primary filled"
                styles={{ backgroundColor: '#000' }}
                size="small"
                color="secondary"
                onClick={() => {
                  /*onClickItem(e)*/
                }}>
                {intl('SCREEN.PO.BUTTON.ADD')}
              </Button>
            )}
          </>
        )}
      </Flex>
    );
  },
  getLabel: (obj) => obj.name || '',
  onEnter: () => {
    // FOR SCAN. ADD ITEM
  },
  deriveRaw: (response) => {
    if (!response) return [];
    if (response.items) return [...response.items.map(deriveRawToItem)];
    return [];
  },
});

const isAndroid = () => typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

const ItemPicker: React.FC<ItemPickerProps> = ({
  disabled,
  scanMode,
  onScanFetch,
  scanOnlyMode = false,
  onSelectItem,
  onClickScan,
  enableBlank = false,
  storeId = 0,
}) => {
  const focusOnSearchBar = () => {
    const el = getEl('.searchInput .react-autosuggest__input');
    if (el) {
      if (scanMode && isAndroid()) {
        el.setAttribute('readonly', 'readonly');
        el.focus();
        // Remove readonly after focus to keep focus but hide keyboard
        setTimeout(() => {
          el.removeAttribute('readonly');
        }, 100);
      } else {
        el.focus();
      }
    }
  };

  // Also handle when scanMode changes while already focused
  useEffect(() => {
    const input = getEl('.searchInput .react-autosuggest__input');
    if (!input) return;

    if (scanMode && isAndroid()) {
      input.setAttribute('readonly', 'readonly');
      input.focus();
      setTimeout(() => {
        input.removeAttribute('readonly');
      }, 100);
    } else {
      input.removeAttribute('readonly');
    }

    return () => {
      input.removeAttribute('readonly');
    };
  }, [scanMode]);

  const handleClickScan = () => {
    focusOnSearchBar();
    onClickScan();
  };

  return (
    <div className={disabled ? 'o50 readonly' : ''}>
      <Flex justifyContent="space-between">
        <Flex className="searchInput" flex={scanOnlyMode ? 1 : 7}>
          <Autocomplete
            className={scanOnlyMode ? 'hidden' : ''}
            hideSearchIcon
            position="top"
            autoSelect={false}
            throttle={scanMode ? 100 : 350}
            fixedList={
              enableBlank
                ? [
                    { ...initialItem, id: 0, itemId: 0, name: 'BLANK' },
                    { ...initialItem, id: 0, itemId: 0, name: 'TEXT' },
                  ]
                : []
            }
            endpoint={scanMode ? configs.api.item.scan : configs.api.item.autocomplete}
            placeholder={intl('INPUT.PLACEHOLDER.SEARCH_ITEM')}
            AC={{
              ...AUTO_COMPLETE(onSelectItem),
              ...(scanMode && {
                onResponse: onScanFetch,
                clearOnFetch: true,
                method: scanMode ? 'POST' : 'GET',
              }),
              clearOnSelect: true,
              extraParam: {
                ...(scanMode
                  ? { format: 'IIIIIIPPPPPV' }
                  : {
                      factoryId: getPublicFactoryId(),
                      withSerial: true,
                      itemOnly: true,
                      startsWith: true,
                    }),
                storeId,
              },
              onSelect: onSelectItem,
            }}
            height="40px"
            width={`calc(100% - ${scanOnlyMode ? '0px' : '80px'})`}
          />

          {scanOnlyMode ? null : (
            <Button onClick={focusOnSearchBar}>{intl('SCREEN.PO.BUTTON.SEARCH')}</Button>
          )}
        </Flex>
        <Div className="scan-barcode" flex={3}>
          <Button onClick={handleClickScan} className="barcode-button">
            <IonIcon icon={barcodeOutline} size="large" />
            {intl('SCREEN.PO.BUTTON.SCAN')}
          </Button>
        </Div>
      </Flex>
    </div>
  );
};

export default ItemPicker;
